'use client';

import { useMemo } from 'react';
import { ClobClient, SignatureTypeV2 } from '@polymarket/clob-client-v2';

import { useAuth } from '@/hooks/useAuth';
import { usePolymarketClient } from '@/hooks/usePolymarketClient';
import { formatBuilderCode, normalizeClobAddress } from '@/utils/clob.utils';

import { CLOB_API_URL, POLY_BUILDER_CODE, POLYGON_CHAIN_ID } from '@/constants/api.constants';

/**
 * useClobClient
 *
 * A specialized hook that provides an authenticated instance of the Polymarket ClobClient.
 * Handles the logic of wrapping an EOA signer to act on behalf of a Gnosis Safe proxy,
 * including EIP-712 domain versioning and signature normalization for V2.
 *
 * @returns An instance of the ClobClient or null if authentication is incomplete.
 */
export function useClobClient(): ClobClient | null {
  const { user, accessToken } = useAuth();
  const { signer } = usePolymarketClient();

  const proxyWalletAddress = user?.proxyWalletAddress;
  const apiKeyCreds = user?.clobCredentials;
  const tradingWalletKind = user?.tradingWalletKind;

  return useMemo(() => {
    // Requirements: Active user session, proxy address, EOA signer, and API credentials
    if (!accessToken || !proxyWalletAddress || !signer || !apiKeyCreds) {
      return null;
    }

    const signatureType =
      tradingWalletKind === 'deposit_wallet'
        ? SignatureTypeV2.POLY_1271
        : SignatureTypeV2.POLY_GNOSIS_SAFE;

    // Initialize the V2 ClobClient
    const client = new ClobClient({
      host: CLOB_API_URL,
      chain: POLYGON_CHAIN_ID,
      signer: signer as any,
      creds: {
        key: apiKeyCreds.apiKey,
        secret: apiKeyCreds.secret,
        passphrase: apiKeyCreds.passphrase,
      },
      signatureType,
      funderAddress: normalizeClobAddress(proxyWalletAddress),
      builderConfig: {
        builderCode: formatBuilderCode(POLY_BUILDER_CODE),
      },
      useServerTime: true,
    });

    // For deposit_wallet, we manually override the orderBuilder's signatureType to POLY_1271.
    // This ensures that while headers use the EOA, the order payload uses the Deposit Wallet
    // for maker/signer fields and signature validation.
    if (tradingWalletKind === 'deposit_wallet') {
      (client.orderBuilder as any).signatureType = SignatureTypeV2.POLY_1271;
    }

    return client;
  }, [accessToken, proxyWalletAddress, signer, apiKeyCreds, tradingWalletKind]);
}
